import React, {
  ComponentType,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import AlertNoticeContext, {
  IAlertNoticeContextState,
} from "./AlertNoticeContext";

/**
 * TBT-FIX: monte le vrai `AlertNoticeProvider` hors du chunk app.js eager.
 *
 * Le chargement du chunk `alert-notice-provider` est piloté par
 * le loader web-performance (injection post-LCP, après `app.js`).
 * Ici on ne fait qu'un `import()` au mount : le module est déjà en cache
 * si le script a été injecté.
 *
 * REMOUNT-FIX: `{children}` reste sous un Context.Provider stable ; le vrai
 * provider monte en sibling. Évite Fragment→Provider qui remountait tout
 * l’arbre (blink pleine page + reload images).
 *
 * Avant montage, les consumers via `useAlertNoticeContext()` recoivent les
 * defaults inertes de `AlertNoticeContext`.
 */
type ProviderComponent = ComponentType<{ children?: ReactNode }>;

const inertNotification: IAlertNoticeContextState["notice"] = {
  open: () => {},
  close: () => {},
  destroy: () => {},
};

const inertValue: IAlertNoticeContextState = {
  displayMsg: () => {},
  notice: inertNotification,
  bottomNotice: inertNotification,
  notices: [],
};

const AlertNoticeDeferredProvider = ({ children }: { children: ReactNode }) => {
  const [Real, setReal] = useState<ProviderComponent | null>(null);
  const [value, setValue] = useState<IAlertNoticeContextState>(inertValue);

  useEffect(() => {
    if (Real) return;
    let cancelled = false;
    void import(
      /* webpackChunkName: "alert-notice-provider" */ "./AlertNoticeProvider"
    ).then((mod) => {
      if (cancelled) return;
      setReal(() => mod.default);
    });
    return () => {
      cancelled = true;
    };
  }, [Real]);

  return (
    <>
      <AlertNoticeContext.Provider value={value}>
        {children}
      </AlertNoticeContext.Provider>
      {Real ? (
        <Real>
          <ContextValueMirror onValue={setValue} />
        </Real>
      ) : null}
    </>
  );
};

function ContextValueMirror({
  onValue,
}: {
  onValue: (value: IAlertNoticeContextState) => void;
}) {
  const ctx = useContext(AlertNoticeContext);
  const prev = useRef<IAlertNoticeContextState | null>(null);
  useEffect(() => {
    const p = prev.current;
    if (
      p &&
      p.displayMsg === ctx.displayMsg &&
      p.notice === ctx.notice &&
      p.bottomNotice === ctx.bottomNotice &&
      p.notices === ctx.notices &&
      p.removeNotice === ctx.removeNotice &&
      p.createNotice === ctx.createNotice
    ) {
      return;
    }
    prev.current = ctx;
    onValue(ctx);
  }, [ctx, onValue]);
  return null;
}

export default AlertNoticeDeferredProvider;
